import React, { useCallback, useRef, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import PhotoGallery from '../PhotoGallery';
import { PhotoModel } from '../../types/protocols';
import { usePhotoUpload } from '../../hooks/usePhotoUpload';
import './RentableDetail.css';

type UploadContext = 'parking' | 'storage_unit' | 'unit';

export type PhotoUploader = (config: { storagePath: (photoKey: string) => string; dbPath: (photoKey: string) => string }) => Promise<unknown>;

/** What the page does with an object's photos; passed to its Redigera form. */
export interface RentablePhotoActions {
    photos: { [key: string]: PhotoModel };
    buildingKey: string;
    onDeletePhoto: (photo: PhotoModel) => Promise<void>;
    onUploadPhotos: (uploader: PhotoUploader) => Promise<void>;
}

/**
 * Photo changes staged in a Redigera form: removals and picked photos are
 * applied by `apply` after Spara, so Avbryt changes nothing.
 */
export function useStagedPhotos(analyticsContext: UploadContext) {
    const [error, setError] = useState<string | null>(null);
    const upload = usePhotoUpload({ analyticsContext, onError: setError });
    const [removedKeys, setRemovedKeys] = useState<string[]>([]);
    const [applying, setApplying] = useState(false);
    const uploadRef = useRef(upload);
    uploadRef.current = upload;

    /** Call when the form opens. */
    const reset = useCallback(() => {
        uploadRef.current.clearPhotos();
        setRemovedKeys([]);
        setError(null);
    }, []);

    /** Removals first, then uploads; each step that succeeds is cleared, so a retry after an error does only the rest. */
    const apply = async (actions: RentablePhotoActions) => {
        setApplying(true);
        try {
            for (const key of removedKeys) {
                const photo = actions.photos[key];
                if (photo) await actions.onDeletePhoto(photo);
                setRemovedKeys(keys => keys.filter(k => k !== key));
            }
            if (upload.photos.length > 0) {
                await actions.onUploadPhotos(config => upload.uploadPhotos(config));
                upload.clearPhotos();
            }
        } finally {
            setApplying(false);
        }
    };

    return {
        upload,
        removedKeys,
        remove: (key: string) => setRemovedKeys(keys => keys.includes(key) ? keys : [...keys, key]),
        undoRemove: () => setRemovedKeys([]),
        error,
        setError,
        reset,
        apply,
        busy: applying || upload.isUploading,
    };
}

export type StagedPhotos = ReturnType<typeof useStagedPhotos>;

interface FieldProps {
    actions: RentablePhotoActions;
    staged: StagedPhotos;
}

/** "Foton" in an object's Redigera form. */
export const RentablePhotosField: React.FC<FieldProps> = ({ actions, staged }) => {
    const { upload, removedKeys } = staged;
    const kept = Object.fromEntries(Object.entries(actions.photos).filter(([key]) => !removedKeys.includes(key)));
    const keptCount = Object.keys(kept).length;
    const removedCount = removedKeys.filter(key => actions.photos[key]).length;

    return (
        <Form.Group className="mb-3">
            <Form.Label>Foton</Form.Label>
            {keptCount > 0 && (
                <PhotoGallery
                    photos={kept}
                    buildingKey={actions.buildingKey}
                    thumbnailSize={90}
                    showDeleteButton
                    onDeleteClick={photo => staged.remove(photo.key)}
                />
            )}
            {upload.photos.length > 0 && (
                <div className="rentable-detail-photo-previews">
                    {upload.photos.map(preview => (
                        <div key={preview.id} className="rentable-detail-photo-preview">
                            <img src={preview.previewUrl} alt={preview.file.name} />
                            <button type="button" onClick={() => upload.removePhoto(preview.id)} title="Ta bort">×</button>
                        </div>
                    ))}
                </div>
            )}
            {keptCount === 0 && upload.photos.length === 0 && <div className="text-muted">Inga foton</div>}
            {removedCount > 0 && (
                <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
                    <span className="text-danger">
                        {removedCount} {removedCount === 1 ? 'foto tas' : 'foton tas'} bort när du sparar
                    </span>
                    <Button variant="outline-secondary" size="sm" onClick={staged.undoRemove}>Ångra</Button>
                </div>
            )}
            <div className="mt-2">
                <Button variant="outline-secondary" size="sm" onClick={upload.openFilePicker} disabled={staged.busy}>
                    Lägg till foton
                </Button>
                <input
                    type="file"
                    accept="image/*"
                    multiple
                    ref={upload.fileInputRef}
                    onChange={upload.handleFileSelect}
                    style={{ display: 'none' }}
                />
            </div>
            {upload.photos.length > 0 && (
                <Form.Text muted>
                    {upload.photos.length} {upload.photos.length === 1 ? 'foto laddas' : 'foton laddas'} upp när du sparar.
                </Form.Text>
            )}
            {staged.error && (
                <Alert variant="danger" className="mt-2 mb-0" dismissible onClose={() => staged.setError(null)}>{staged.error}</Alert>
            )}
        </Form.Group>
    );
};
